import { findLogService } from "@cervval.js/common/src/services/log.service";
import { getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { CollectionTools, anyReallyAny } from "@cervval.js/common/tools";
import { ActionPersistData } from "../action.model";
import { ActionCacheService } from "./cache.service";

//#region Apply ActionPersistData to cache

/** 
 * Apply changes contained by the ActionPersistData to the cache.
 * 
 * ## Why did we implemented this method ?
 * 
 * An ActionPersistData is typically generated by the ActionService and
 * sent to the ActionPersistenceService.
 * 
 * DO NOT use this function to perform direct modification on the cache. 
 * This method is to update the cache from an ActionPersistData that comes 
 * from another user, the server, another window, ...
 */
export function applyChanges<ElementTypes>(cache: ActionCacheService<ElementTypes>, actionPersistData: ActionPersistData<unknown>): void {
    // -- Elements creation ---------------------------------------------------
    if (actionPersistData?.elementsToCreate) {
        for (const op of actionPersistData.elementsToCreate) {
            const repository = cache.getDTOCacheRepository(op.elementTypeName as keyof ElementTypes);
            if (!op.element) {
                throw new Error(`Cannot create empty element in ${op.elementTypeName}`);
            }
            repository.push(op.element as anyReallyAny);
        }
    }

    // -- Elements deletion ---------------------------------------------------
    if (actionPersistData?.elementsToDelete) {
        for (const op of actionPersistData.elementsToDelete) {
            const repository = cache.getDTOCacheRepository(op.elementTypeName as keyof ElementTypes);
            const dto = repository.findDTOById(op.elementId);
            if (!dto) {
                // Here, we skip the modification if we don't have the DTO.
                // This is mostly due to the fact that we may have a partial cache
                continue;
            }
            // There is no need to handle markDeletion because this is only in server sql manager
            repository.remove(dto);
        }
    }

    // -- Elements updates ----------------------------------------------------
    if (actionPersistData?.elementsToUpdate) {
        for (const op of actionPersistData.elementsToUpdate) {
            const repository = cache.getDTOCacheRepository(op.elementTypeName as keyof ElementTypes);
            const dto = repository.findDTOById(op.elementId);
            if (!dto) {
                // Here, we skip the modification if we don't have the DTO.
                // This is mostly due to the fact that we may have a partial cache
                continue;
            }

            // -- Fields to update --
            for (const field in op.fieldsToUpdate) {
                setProp(dto, field, op.fieldsToUpdate[field]);
            }

            // -- Add items to a list --
            if (op.valuesToAdd) {
                for (const field of objectKeys(op.valuesToAdd)) {
                    let list = getProp(dto, field) as unknown as unknown[] | undefined;
                    if (list == null) {
                        list = [];
                    }
                    if (!Array.isArray(list)) {
                        findLogService()?.error(op.elementTypeName + "." + field + " is not an array for " + op.elementId);
                        continue;
                    }
                    for (let i = 0; i < op.valuesToAdd[field].length; i++) {
                        const value = op.valuesToAdd[field][i];
                        // Arrays are handled as Set, value is never pushed twice
                        CollectionTools.pushIfNew(list, value);
                    }
                    setProp(dto, field, list);
                }
            }

            // -- Remove items from a list --
            if (op.valuesToRemove) {
                for (const field of objectKeys(op.valuesToRemove)) {
                    const list = getProp(dto, field) as unknown as unknown[] | undefined;
                    if (list == null) {
                        // do not throw error in undo process, simply log error
                        findLogService()?.error(op.elementTypeName + "." + field + " is not an array for " + op.elementId);
                        continue;
                    }
                    CollectionTools.removeElementsFromArray(list, op.valuesToRemove[field]);
                }
            }
        }
    }

}

//#endregion

//#region Check if repositories are "dirty"

/** 
 * Check if repositories are "dirty"
 * @param cache cache service
 * @param elementTypeNames if provided only these repos are checked
 * @returns true if cache is dirty 
 */
export function isDirty<ElementTypes>(cache: ActionCacheService<ElementTypes>, elementTypeNames?: (keyof ElementTypes)[]): boolean {
    const typeNames = elementTypeNames ?? Array.from(cache.getTableDefinitionMap().keys());
    for (const t of typeNames) {
        const repo = cache.findDTOCacheRepository(t);
        if (repo?.lastSetData && repo.isDirty()) {
            // data was set but is dirty
            return true;
        }
    }
    return false;
}

//#endregion

//#region Update foreign keys values

/**
 * This function updates "value" if the id was updated
 * You can call it with any field of the object. It will only be updated if its a negative id.
 * @param cache The action cache (action.getCacheService())
 * @param foreignTable The foreign table. Use directly the result of getForeignKeyTableName().
 * @param value The value to update. Will only be updated if it is an id and there is a new value available
 * @param optionalUpdate This function can automatically update a dto, you just need to provide it
 * @param optionalUpdate.field Field name. It must be an existing field, else it may throw errors if it does not find information.
 * @param optionalUpdate.dto Optional. If passed, its field will be updated to the new value (if it has the old value)
 * @returns New value of the id if it exists, old value otherwise.
 */
export function actualizeFieldValueIfNeeded<ElementTypes, TableName extends keyof ElementTypes>(cache: ActionCacheService<ElementTypes>, foreignTable: keyof ElementTypes | null, value: anyReallyAny, optionalUpdate?: {
    dto: ElementTypes[TableName] | null | undefined,
    field: string
}): anyReallyAny {
    if (typeof value !== "number" || foreignTable == null) {
        // Quick exit, foreign keys are always numbers
        return value;
    }
    // Get the new value of the id
    const newValueUpdated: anyReallyAny = cache.getDTOCacheRepository(foreignTable).getUpToDateId(value);
    if (optionalUpdate?.dto != null && getProp(optionalUpdate.dto, optionalUpdate.field) === value && value !== newValueUpdated) {
        // a dto was provided and it still has the old id, we update to the up to date id
        setProp(optionalUpdate.dto, optionalUpdate.field, newValueUpdated);
    }
    return newValueUpdated;
}

//#endregion
